import React from 'react'
import { makeStyles, Tooltip } from '@paintscout/ui'
import type { TooltipProps, MuiTheme as Theme, StyleClasses } from '@paintscout/ui'

const useStyles = makeStyles<Theme, PresEditorTooltipProps>(
  (theme) => ({
    tooltip: {
      ...theme.typography.subtitle2,
      color: theme.palette.common.white,
      fontSize: '0.8rem'
    }
  }),
  { name: 'PresEditorTooltip' }
)

export interface PresEditorTooltipProps extends TooltipProps {
  classes?: StyleClasses<typeof useStyles>
}

function PresEditorTooltip(props: PresEditorTooltipProps) {
  const classes = useStyles(props)
  const { children, ...rest } = props

  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)

  // Callers set the disable*Listener props while their popup menu is open, but MUI schedules
  // opens on an enterDelay timer, so one can still fire after the listeners are disabled —
  // suppress those late opens too, and close anything already showing.
  const suppressed = Boolean(rest.disableHoverListener || rest.disableFocusListener || rest.disableTouchListener)
  const suppressedRef = React.useRef(suppressed)
  suppressedRef.current = suppressed

  React.useEffect(() => {
    if (suppressed) setOpen(false)
  }, [suppressed])

  /**
   * #22675: the tooltip's open state can outlive the *visibility* of its anchor.
   *
   * Editor chrome (AddBlockButton, BlockMenu, SectionDividerMenu) is hidden with `display: none`
   * by a CSS `:hover` rule rather than unmounted — see `addBlockButton` in Presentation.tsx. A
   * hidden anchor can never receive `mouseleave` or `blur`, and MUI's handleLeave is the only
   * route to `onClose`, so `open` stays true forever. Its rect is also 0x0, so Popper strands the
   * portaled tooltip at the viewport origin, on top of the sidebar, with nothing to dismiss it.
   *
   * Both guards below only ever close, so they cannot re-open a suppressed tooltip.
   */
  React.useEffect(() => {
    if (!open) return

    const anchor = anchorRef.current
    if (!anchor) return

    // the pointer moved off the anchor without the anchor seeing a mouseleave
    const handleDocumentMouseMove = (ev: MouseEvent) => {
      if (anchor.contains(ev.target as Node)) return
      // a keyboard-focus tooltip must survive a stray mouse move
      if (document.activeElement && anchor.contains(document.activeElement)) return
      setOpen(false)
    }
    document.addEventListener('mousemove', handleDocumentMouseMove, true)

    // the anchor collapsed to 0x0 (an ancestor got `display: none`) with no pointer event at all.
    // ResizeObserver fires once on observe(), so this also catches an anchor that was already
    // hidden when a late enterDelay timer opened the tooltip.
    //
    // `entries` can be empty, so the `?.` is load-bearing — do not "simplify" it away.
    //
    // A zero box alone is not proof of hiding: this div is sized by its children, and the shared
    // Tooltip's Badge wrapper (docs/pitfalls.md) can leave it 0x0 while it still renders.
    // getClientRects() is the precise test — it is empty only for `display: none`, and a rendered
    // zero-size element still reports one rect. jsdom has no layout engine and always returns an
    // empty list, so the entry rect is what discriminates under test.
    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect
        if (!rect || rect.width !== 0 || rect.height !== 0) return
        if (anchor.getClientRects().length === 0) setOpen(false)
      })
      observer.observe(anchor)
    }

    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove, true)
      observer?.disconnect()
    }
  }, [open])

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = (ev) => {
    if (suppressedRef.current) {
      return null
    }
    if (ev.target.className.includes && ev.target.className.includes('MuiPopover')) {
      return null
    } else setOpen(true)
  }

  return (
    <Tooltip
      hideIcon
      enterDelay={800}
      enterNextDelay={400}
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
      placement={'bottom'}
      {...rest}
      classes={{ tooltip: classes.tooltip, ...rest.classes }}
    >
      <div ref={anchorRef} onMouseDown={handleClose}>
        {children}
      </div>
    </Tooltip>
  )
}

export default PresEditorTooltip
